import type {
  FieldLogFootageReviewAction,
  FieldLogFootageReviewResult,
  FieldLogFootageStatus,
  FieldLogFootageStatusReason,
} from '@/features/field-log/types/fieldLog';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import {
  Check,
  Link,
  Link2,
  type LucideIcon,
  RotateCcw,
  SearchCheck,
  Video,
  VideoOff,
} from 'lucide-react';

export const FIELD_LOG_FOOTAGE_STATUSES = [
  'recording',
  'completed',
  'no_video',
  'review_match',
  'linked',
  'link_manually',
] as const satisfies readonly FieldLogFootageStatus[];

/** Review statuses offered by the shared user-facing footage filter. */
export const FIELD_LOG_FOOTAGE_FILTER_STATUSES = [
  'completed',
  'review_match',
  'linked',
  'link_manually',
] as const satisfies readonly FieldLogFootageStatus[];

export const FIELD_LOG_FOOTAGE_STATUS_MESSAGES: Record<FieldLogFootageStatus, MessageDescriptor> = {
  recording: msg`Recording`,
  completed: msg`Completed`,
  no_video: msg`No video`,
  review_match: msg`Review match`,
  link_manually: msg`Link manually`,
  linked: msg`Linked`,
};

export const FIELD_LOG_FOOTAGE_STATUS_HINTS: Record<FieldLogFootageStatus, MessageDescriptor> = {
  recording: msg`This session is still recording.`,
  completed: msg`Someone confirmed that all footage has been reviewed.`,
  no_video: msg`Someone confirmed that this session has no video.`,
  review_match: msg`A possible video was found. Review the match.`,
  link_manually: msg`No usable match was found. Find the footage manually.`,
  linked: msg`Video footage is linked. Confirm complete after all footage has been reviewed.`,
};

export const FIELD_LOG_FOOTAGE_STATUS_REASONS: Record<
  FieldLogFootageStatusReason,
  MessageDescriptor
> = {
  matching_blocked: msg`Matching could not run. Check the recording details or link it manually.`,
  matching_incomplete: msg`Matching is not finished yet. Check again later or link it manually.`,
  linked_elsewhere: msg`The possible video is linked to another session.`,
  no_candidates: msg`No possible video was found.`,
};

export const FIELD_LOG_FOOTAGE_STATUS_ACTIONS: Record<FieldLogFootageStatus, MessageDescriptor> = {
  recording: msg`Recording`,
  completed: msg`Manage footage`,
  no_video: msg`Manage footage`,
  review_match: msg`Review match`,
  link_manually: msg`Find footage`,
  linked: msg`Manage footage`,
};

export const FIELD_LOG_FOOTAGE_STATUS_ICONS: Record<FieldLogFootageStatus, LucideIcon> = {
  recording: Video,
  completed: Check,
  no_video: VideoOff,
  review_match: SearchCheck,
  link_manually: Link,
  linked: Link2,
};

export const FIELD_LOG_FOOTAGE_STATUS_BADGE_VARIANTS = {
  recording: 'outline',
  completed: 'success',
  no_video: 'muted',
  review_match: 'warning',
  link_manually: 'outline',
  linked: 'success',
} as const satisfies Record<FieldLogFootageStatus, 'outline' | 'success' | 'muted' | 'warning'>;

export const FIELD_LOG_REVIEW_ACTION_RESULTS: Record<
  FieldLogFootageReviewAction,
  FieldLogFootageReviewResult
> = {
  confirm_complete: 'completed',
  mark_no_video: 'no_video',
  reopen: 'pending',
};

export const FIELD_LOG_REVIEW_ACTION_SOURCE_RESULTS = {
  confirm_complete: ['pending'],
  mark_no_video: ['pending'],
  reopen: ['completed', 'no_video'],
} as const satisfies Record<FieldLogFootageReviewAction, readonly FieldLogFootageReviewResult[]>;

export const FIELD_LOG_REVIEW_ACTION_LABELS: Record<
  FieldLogFootageReviewAction,
  MessageDescriptor
> = {
  confirm_complete: msg`Confirm complete`,
  mark_no_video: msg`Mark no video`,
  reopen: msg`Reopen review`,
};

export const FIELD_LOG_REVIEW_ACTION_TITLES: Record<
  FieldLogFootageReviewAction,
  MessageDescriptor
> = {
  confirm_complete: msg`Confirm that all footage has been reviewed?`,
  mark_no_video: msg`Confirm that this session has no video?`,
  reopen: msg`Reopen this footage review?`,
};

export const FIELD_LOG_REVIEW_ACTION_DESCRIPTIONS: Record<
  FieldLogFootageReviewAction,
  MessageDescriptor
> = {
  confirm_complete: msg`This marks the session as completed for everyone.`,
  mark_no_video: msg`Use this only when the recording produced no video.`,
  reopen: msg`The session will return to the review queue.`,
};

export const FIELD_LOG_REVIEW_ACTION_SUCCESS: Record<
  FieldLogFootageReviewAction,
  MessageDescriptor
> = {
  confirm_complete: msg`Footage review completed.`,
  mark_no_video: msg`Session marked as no video.`,
  reopen: msg`Footage review reopened.`,
};

export const FIELD_LOG_REVIEW_ACTION_ICONS: Record<FieldLogFootageReviewAction, LucideIcon> = {
  confirm_complete: Check,
  mark_no_video: VideoOff,
  reopen: RotateCcw,
};

export const FIELD_LOG_REVIEW_ERROR_MESSAGES: Record<string, MessageDescriptor> = {
  review_state_conflict: msg`The session can no longer be reviewed in that way. Refresh and try again.`,
  recording_in_progress: msg`Wait for the recording to end before reviewing it.`,
  no_linked_footage: msg`Link at least one video before confirming completion.`,
  linked_footage_exists: msg`This session already has linked footage and cannot be marked as no video.`,
  review_version_exhausted: msg`This review has reached its version limit and can no longer be changed. Contact support.`,
  review_version_conflict: msg`This review changed elsewhere. Check the refreshed status and try again.`,
  idempotency_key_reused: msg`This review request was already used with different details. Try again.`,
};
